import React, { useEffect } from 'react';
import { Button, ButtonGroup, Modal } from '@ver-uds/react';
import { FormProvider, useForm } from 'react-hook-form';
import EVerifyUsersModalForm from './EVerifyUsersModalForm';
import EVerifyUser from './data/EVerifyUser';
import SubmitAction from '../SubmitAction';

export interface ModalSubmitData {
  action: SubmitAction;
  user: EVerifyUser;
}

export type EVerifyUserModalProps = {
  user?: EVerifyUser;
  addButtonText: string;
  foundMouSignatoryId: string | undefined;
  showUserRoleOptions: boolean;
  modalHeaderText?: string;
  open: boolean;
  close(): void;
  onSubmit: (data: ModalSubmitData) => void;
};

const EVerifyUserModal = ({
  user,
  addButtonText,
  foundMouSignatoryId,
  showUserRoleOptions,
  modalHeaderText,
  open,
  close,
  onSubmit,
}: EVerifyUserModalProps): JSX.Element => {
  const modalId = 'everify-user-modal';
  const formId = `${modalId}-form`;
  const customModalHeader = modalHeaderText || 'E-Verify User';
  const modalHeader = `${user ? 'Edit' : 'Add'} ${customModalHeader}`;

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, reset, ...methods } = useForm<EVerifyUser>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      emailAddress: user?.emailAddress ?? '',
      firstName: user?.firstName,
      lastName: user?.lastName ?? '',
      middleInitial: user?.middleInitial ?? '',
      phoneNumber: user?.phoneNumber ?? '',
      phoneNumberExtension: user?.phoneNumberExtension ?? '',
      mouSignatory: user?.mouSignatory ?? '',
      admin: user?.admin ?? '',
    },
  });

  // Set the default form values when we've loaded them.
  useEffect(() => {
    reset({
      emailAddress: user?.emailAddress ?? '',
      firstName: user?.firstName,
      lastName: user?.lastName ?? '',
      middleInitial: user?.middleInitial ?? '',
      phoneNumber: user?.phoneNumber ?? '',
      phoneNumberExtension: user?.phoneNumberExtension ?? '',
      mouSignatory: user?.mouSignatory ?? '',
      admin: user?.admin ?? '',
    });
  }, [user, reset]);

  const onFormSubmit = handleSubmit((data) => {
    if (!showUserRoleOptions || data.mouSignatory === 'Yes' || data.admin === 'Yes') {
      onSubmit({
        action: user ? SubmitAction.UPDATE : SubmitAction.ADD,
        user: {
          ...data,
          id: user?.id || '',
          mouSignatory: showUserRoleOptions ? data.mouSignatory : 'No',
          admin: showUserRoleOptions ? data.admin : 'Yes',
        },
      });
    }
  });

  return (
    <Modal id={modalId} header={modalHeader} open={open} close={close} fixedToTop>
      <Modal.Content>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <FormProvider handleSubmit={handleSubmit} reset={reset} {...methods}>
          <EVerifyUsersModalForm
            id={formId}
            showUserRoleOptions={showUserRoleOptions}
            user={user}
            foundMouSignatoryId={foundMouSignatoryId}
            onSubmit={onFormSubmit}
          />
        </FormProvider>
      </Modal.Content>
      <Modal.Footer>
        <ButtonGroup>
          <Button type="button" variant="secondary" onClick={close}>
            Cancel
          </Button>
          {user ? (
            <Button type="submit" variant="primary" form={formId} onClick={onFormSubmit}>
              Save
            </Button>
          ) : (
            <Button
              data-testid="modal-add-user-button"
              type="submit"
              variant="submit"
              form={formId}
              onClick={onFormSubmit}
            >
              {addButtonText}
            </Button>
          )}
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default EVerifyUserModal;
